/**
 * TeamCard Component
 * Combined Sales + Design team in one compact card
 * Merges the previous separate Sales Team and Design Team cards
 */

import React, { useState } from 'react';
import { getProjectSid } from '../../utils/projectHelpers';
import { hasDirectorAccess } from '../../utils/rolePermissions';
import TeamRoleSection from './TeamRoleSection';
import TeamHistorySection from './TeamHistorySection';
import SalesHistorySection from './SalesHistorySection';

// Helper to get nickname (fallback to fullName)
const getNickname = (user) => {
  if (!user) return 'N/A';
  return user.nickname || user.fullName || 'Unknown';
};

const TeamCard = ({
  project,
  userRole,
  onOpenTeamAssignModal,
  onOpenSalesAgentModal,
  onOpenAssignSidModal,
  onRemoveTeamMember,
  onRemoveSalesAgent,
  onSetPrimaryAgent,
  removingTeamMember,
  removingSalesAgent,
  settingPrimaryAgent,
  onRefresh,
  viewOnly = false
}) => {
  const [expandedSection, setExpandedSection] = useState(null); // 'sales' or 'design' or null

  const isManager = userRole === 'manager';
  const isDirectorLevel = hasDirectorAccess(userRole);
  const isLeader = userRole === 'leader';
  const isSidOrAsid = ['sid', 'asid'].includes(userRole);
  // In view-only mode, no one can manage
  const canManageTeam = !viewOnly && (isManager || isDirectorLevel || isLeader || isSidOrAsid);
  const canManageSales = !viewOnly && (isManager || isDirectorLevel || isLeader);

  // Check if role needs assignment (show prompt)
  const needsAssignment = (roleType) => {
    if (viewOnly || !canManageTeam) return false;
    const hasAssignment = project?.teamMembers?.some(m => m.roleType === roleType);
    return !hasAssignment;
  };

  const needs2DAssignment = needsAssignment('draughter_2d');
  const needs3DAssignment = needsAssignment('designer_3d');

  // Helper to get team members by role
  const getTeamMembersByRole = (roleType) => {
    if (!project?.teamMembers) return [];
    return project.teamMembers.filter(m => m.roleType === roleType);
  };

  const projectSid = getProjectSid(project);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        Team
      </h2>

      {/* Sales Section */}
      <div className="mb-4 pb-4 border-b border-gray-100">
        <div
          className="flex items-center justify-between cursor-pointer min-h-[44px] -mx-2 px-2 rounded-lg hover:bg-gray-50 touch-manipulation"
          onClick={() => setExpandedSection(expandedSection === 'sales' ? null : 'sales')}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-green-700">Sales</span>
            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex-shrink-0">
              {(project.salesAgents?.length || 0) + (project.assignment?.agent ? 1 : 0)}
            </span>
            {/* Sales Leader name in header */}
            {project.assignment?.assignedLeader && (
              <span className="text-xs text-gray-500 truncate">
                - {getNickname(project.assignment.assignedLeader)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* History button - touch friendly */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSection(expandedSection === 'salesHistory' ? null : 'salesHistory');
              }}
              className={`text-xs px-2.5 py-1.5 rounded-md transition-colors min-h-[36px] touch-manipulation ${
                expandedSection === 'salesHistory'
                  ? 'bg-gray-200 text-gray-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="View assignment history"
            >
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">History</span>
              </span>
            </button>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expandedSection === 'sales' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Compact Sales View */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Sales Leader */}
          {project.assignment?.assignedLeader && (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full">
              <span className="text-xs text-green-600">Leader:</span>
              <span className="text-xs font-medium text-green-800">
                {getNickname(project.assignment.assignedLeader)}
              </span>
            </div>
          )}

          {/* Case Closer */}
          {(project.salesForm?.agent || project.assignment?.agent) && (
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
              <span className="text-xs text-gray-600">Closer:</span>
              <span className="text-xs font-medium text-gray-800">
                {getNickname(project.salesForm?.agent || project.assignment?.agent)}
              </span>
            </div>
          )}

          {/* Agent names */}
          {project.salesAgents?.map(assignment => (
            <div
              key={assignment.id}
              className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                assignment.isPrimary ? 'bg-yellow-100' : 'bg-green-50'
              }`}
            >
              <span className={`text-xs font-medium ${assignment.isPrimary ? 'text-yellow-700' : 'text-green-700'}`}>
                {getNickname(assignment.agent)}
              </span>
              {assignment.isPrimary && (
                <span className="text-xs text-yellow-600">(P)</span>
              )}
            </div>
          ))}

          {canManageSales && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenSalesAgentModal?.();
              }}
              className="text-xs px-3 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 active:scale-95 transition-all min-h-[36px] touch-manipulation"
            >
              + Add
            </button>
          )}
        </div>

        {/* Expanded Sales View */}
        {expandedSection === 'sales' && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {project.salesAgents?.map(assignment => (
              <div key={assignment.id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-900">
                      {getNickname(assignment.agent)}
                    </span>
                    {assignment.isPrimary && (
                      <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">Primary</span>
                    )}
                  </div>
                </div>
                {canManageSales && (
                  <div className="flex items-center gap-1">
                    {!assignment.isPrimary && (
                      <button
                        onClick={() => onSetPrimaryAgent?.(assignment.id)}
                        disabled={settingPrimaryAgent === assignment.id}
                        className="p-1 text-gray-400 hover:text-yellow-600 rounded"
                        title="Set as primary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => onRemoveSalesAgent?.(assignment.id)}
                      disabled={removingSalesAgent === assignment.id}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"
                      title="Remove"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Expanded Sales History View */}
        {expandedSection === 'salesHistory' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <SalesHistorySection projectId={project.id} />
          </div>
        )}
      </div>

      {/* Assignment Prompts - Show when team member assignment is needed */}
      {(needs2DAssignment || needs3DAssignment) && (
        <div className="mb-4 pb-4 border-b border-gray-100 space-y-2">
          {needs2DAssignment && (
            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium text-blue-800">2D Draughter needed</span>
              </div>
              <button
                onClick={() => onOpenTeamAssignModal?.('draughter_2d')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Assign
              </button>
            </div>
          )}
          {needs3DAssignment && (
            <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium text-purple-800">3D Designer needed</span>
              </div>
              <button
                onClick={() => onOpenTeamAssignModal?.('designer_3d')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Assign
              </button>
            </div>
          )}
        </div>
      )}

      {/* Design Section */}
      <div>
        <div
          className="flex items-center justify-between cursor-pointer min-h-[44px] -mx-2 px-2 rounded-lg hover:bg-gray-50 touch-manipulation"
          onClick={() => setExpandedSection(expandedSection === 'design' ? null : 'design')}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-purple-700">Design</span>
            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded flex-shrink-0">
              {project?.teamMembers?.length || 0}
            </span>
            {/* SID name in header - show supervisor SID for ASIDs, or the SID directly */}
            {projectSid && (
              <span className="text-xs text-gray-500 truncate">
                - {projectSid.role === 'asid' && projectSid.supervisor
                    ? getNickname(projectSid.supervisor)
                    : getNickname(projectSid)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* History button - touch friendly */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSection(expandedSection === 'history' ? null : 'history');
              }}
              className={`text-xs px-2.5 py-1.5 rounded-md transition-colors min-h-[36px] touch-manipulation ${
                expandedSection === 'history'
                  ? 'bg-gray-200 text-gray-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="View assignment history"
            >
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">History</span>
              </span>
            </button>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expandedSection === 'design' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Compact Design View */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* SID/ASID Handler */}
          {projectSid ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded-full">
              <span className={`text-xs ${projectSid.role === 'sid' ? 'text-purple-600' : 'text-blue-600'}`}>
                {projectSid.role === 'sid' ? 'SID' : 'ASID'}:
              </span>
              <span className="text-xs font-medium text-purple-800">
                {getNickname(projectSid)}
              </span>
              {(isManager || isDirectorLevel || isLeader) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAssignSidModal?.();
                  }}
                  className="ml-1 text-purple-500 hover:text-purple-700"
                  title="Change SID"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            (isManager || isDirectorLevel || isLeader) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAssignSidModal?.();
                }}
                className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Assign SID
              </button>
            )
          )}

          {/* Team role indicators */}
          {['site_draughter', 'draughter_2d', 'designer_3d'].map(roleType => {
            const members = getTeamMembersByRole(roleType);
            if (members.length === 0) return null;
            const labels = {
              site_draughter: 'Site',
              draughter_2d: '2D',
              designer_3d: '3D'
            };
            const colorClasses = {
              site_draughter: { text: 'text-orange-600', bg: 'bg-orange-50', name: 'text-orange-700', btn: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
              draughter_2d: { text: 'text-blue-600', bg: 'bg-blue-50', name: 'text-blue-700', btn: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
              designer_3d: { text: 'text-purple-600', bg: 'bg-purple-50', name: 'text-purple-700', btn: 'bg-purple-100 text-purple-700 hover:bg-purple-200' }
            };
            const colors = colorClasses[roleType];
            return (
              <div key={roleType} className="flex items-center gap-1">
                <span className={`text-xs ${colors.text}`}>{labels[roleType]}:</span>
                {members.map(m => (
                  <span
                    key={m.id}
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.name}`}
                  >
                    {getNickname(m.user)}
                  </span>
                ))}
                {/* Manage button for managers - touch friendly */}
                {canManageTeam && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenTeamAssignModal?.(roleType);
                    }}
                    className={`text-xs px-2.5 py-1.5 rounded-md transition-all active:scale-95 min-h-[32px] touch-manipulation ${colors.btn}`}
                    title={`Manage ${labels[roleType]} assignments`}
                  >
                    Manage
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Expanded Design View */}
        {expandedSection === 'design' && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
            <TeamRoleSection
              title="Site Draughters"
              roleType="site_draughter"
              members={getTeamMembersByRole('site_draughter')}
              color="orange"
              canManage={canManageTeam}
              onAdd={() => onOpenTeamAssignModal?.('site_draughter')}
              onRemove={onRemoveTeamMember}
              removingId={removingTeamMember}
            />
            <TeamRoleSection
              title="2D Draughters"
              roleType="draughter_2d"
              members={getTeamMembersByRole('draughter_2d')}
              color="blue"
              canManage={canManageTeam}
              onAdd={() => onOpenTeamAssignModal?.('draughter_2d')}
              onRemove={onRemoveTeamMember}
              removingId={removingTeamMember}
            />
            <TeamRoleSection
              title="3D Designers"
              roleType="designer_3d"
              members={getTeamMembersByRole('designer_3d')}
              color="purple"
              canManage={canManageTeam}
              onAdd={() => onOpenTeamAssignModal?.('designer_3d')}
              onRemove={onRemoveTeamMember}
              removingId={removingTeamMember}
            />
          </div>
        )}

        {/* Expanded History View */}
        {expandedSection === 'history' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <TeamHistorySection projectId={project.id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamCard;
